/**
 * Deep Link Service
 * Handles parsing and routing of deep links for the Smart Counsellor app
 */

import type { NavigateFunction } from 'react-router-dom';
import { authService } from './authService';

export interface DeepLinkRoute {
  path: string;
  requiresAuth: boolean;
  allowedRoles?: string[];
}

export interface ParsedDeepLink {
  route: string;
  params: Record<string, string>;
  queryParams: URLSearchParams;
}

class DeepLinkService {
  private navigate: NavigateFunction | null = null;

  /**
   * Initialize the deep link service with navigation function
   */
  initialize(navigate: NavigateFunction) {
    this.navigate = navigate;
  }

  /**
   * Parse a deep link URL and extract route information
   */
  parseDeepLink(url: string): ParsedDeepLink {
    try {
      const urlObj = new URL(url);
      
      // Handle custom scheme URLs (smartcounsellor://)
      if (urlObj.protocol === 'smartcounsellor:') {
        const path = urlObj.pathname || urlObj.host || '/';
        return {
          route: path.startsWith('/') ? path : `/${path}`,
          params: {},
          queryParams: urlObj.searchParams
        };
      }
      
      // Handle universal links (https://smartcounsellor.netlify.app/...)
      if (urlObj.protocol === 'https:' || urlObj.protocol === 'http:') {
        return {
          route: urlObj.pathname,
          params: {},
          queryParams: urlObj.searchParams
        };
      }
      
      // Fallback to dashboard
      return {
        route: '/dashboard',
        params: {},
        queryParams: new URLSearchParams()
      };
    } catch (error) {
      console.error('Failed to parse deep link:', error);
      return {
        route: '/dashboard',
        params: {},
        queryParams: new URLSearchParams()
      };
    }
  }

  /**
   * Handle incoming deep link
   */
  async handleDeepLink(url: string): Promise<boolean> {
    try {
      if (!this.navigate) {
        console.error('Deep link service not initialized');
        return false;
      }

      //console.log('Handling deep link:', url);
      
      const parsed = this.parseDeepLink(url);
      //console.log('Parsed deep link:', parsed);

      // Check if user is authenticated for protected routes
      const isAuthenticated = authService.isAuthenticated();
      const userRole = authService.getUserRole();

      // Define route access rules
      const protectedRoutes = [
        '/dashboard',
        '/students',
        '/cases',
        '/schools',
        '/reports',
        '/activities',
        '/admin'
      ];

      const seniorCounsellorRoutes = ['/schools'];

      // Check if route requires authentication
      const requiresAuth = protectedRoutes.some(route => 
        parsed.route.startsWith(route)
      );

      if (requiresAuth && !isAuthenticated) {
        // Redirect to login and store intended destination
        sessionStorage.setItem('deepLinkRedirect', url);
        this.navigate('/login');
        return true;
      }

      // Check role-based access
      if (parsed.route.startsWith('/admin') && 
          userRole && !['super_admin', 'senior_counsellor'].includes(userRole)) {
        this.navigate('/dashboard');
        return true;
      }

      if (seniorCounsellorRoutes.some(route => parsed.route.startsWith(route)) && 
          userRole && !['super_admin', 'senior_counsellor'].includes(userRole)) {
        this.navigate('/dashboard');
        return true;
      }

      // Handle specific routes with parameters
      if (parsed.route.startsWith('/cases/') && parsed.route.length > 7) {
        const caseId = parsed.route.split('/')[2];
        const tab = parsed.queryParams.get('tab');
        let navigationPath = `/cases/${caseId}`;
        if (tab) {
          navigationPath += `?tab=${tab}`;
        }
        this.navigate(navigationPath);
        return true;
      }

      if (parsed.route.startsWith('/students/') && parsed.route.length > 10) {
        const studentId = parsed.route.split('/')[2];
        this.navigate(`/students/${studentId}`);
        return true;
      }

      // Handle general routes
      switch (parsed.route) {
        case '/':
        case '/dashboard':
          this.navigate('/dashboard');
          break;
        
        case '/students':
          this.navigate('/students');
          break;
        
        case '/cases':
          this.navigate('/cases');
          break;
        
        case '/schools':
          this.navigate('/schools');
          break;
        
        case '/reports':
          this.navigate('/reports');
          break;
        
        case '/activities':
          this.navigate('/activities');
          break;
        
        case '/admin':
          this.navigate('/admin');
          break;
        
        case '/login':
          if (await isAuthenticated) {
            this.navigate('/dashboard');
          } else {
            this.navigate('/login');
          }
          break;
        
        default:
          // Unknown route, redirect to dashboard
          this.navigate('/dashboard');
      }

      return true;
    } catch (error) {
      console.error('Error handling deep link:', error);
      // Fallback to dashboard on any error
      if (this.navigate) {
        this.navigate('/dashboard');
      }
      return false;
    }
  }

  /**
   * Generate shareable deep links
   */
  generateDeepLink(route: string, params?: Record<string, string>): string {
    const baseUrl = 'https://smartcounsellor.netlify.app';
    let url = `${baseUrl}${route}`;
    
    if (params && Object.keys(params).length > 0) {
      const searchParams = new URLSearchParams(params);
      url += `?${searchParams.toString()}`;
    }
    
    return url;
  }

  /**
   * Generate custom scheme deep links (for testing)
   */
  generateCustomSchemeLink(route: string, params?: Record<string, string>): string {
    let url = `smartcounsellor://${route}`;
    
    if (params && Object.keys(params).length > 0) {
      const searchParams = new URLSearchParams(params);
      url += `?${searchParams.toString()}`;
    }
    
    return url;
  }

  /**
   * Handle redirect after successful login
   */
  handleLoginRedirect(): boolean {
    try {
      const storedRedirect = sessionStorage.getItem('deepLinkRedirect');
      if (storedRedirect) {
        sessionStorage.removeItem('deepLinkRedirect');
        // Use setTimeout to ensure navigation happens after current render cycle
        setTimeout(() => {
          this.handleDeepLink(storedRedirect);
        }, 100);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error handling login redirect:', error);
      return false;
    }
  }
}

export const deepLinkService = new DeepLinkService();
